import { useEffect, useRef, useState } from 'react'

const NODES = [
  {
    badge: '01',
    title: 'Signal pickup',
    desc: 'Velocity, geo, device and behavior signals scored in parallel.',
  },
  {
    badge: '02',
    title: 'Scenario rollup',
    desc: 'Signals form fraud patterns: mule rings, smurfing, account takeover.',
  },
  {
    badge: '03',
    title: 'Decision with receipts',
    desc: 'Every verdict ships with its full reasoning chain attached, line by line.',
  },
]

export default function JetStreamSection() {
  const trackRef = useRef(null)
  const [shown, setShown] = useState({ n: [false, false, false], a: [false, false] })

  useEffect(() => {
    const track = trackRef.current
    if (!track) return
    const update = () => {
      const r = track.getBoundingClientRect()
      const total = r.height - window.innerHeight
      const passed = -r.top
      const p = Math.max(0, Math.min(1, passed / total))
      setShown((prev) => {
        const next = { n: [...prev.n], a: [...prev.a] }
        const apply = (arr, idx, showAt, hideAt) => {
          if (p > showAt) arr[idx] = true
          else if (p < hideAt) arr[idx] = false
        }
        apply(next.n, 0, 0.10, 0.05)
        apply(next.a, 0, 0.30, 0.25)
        apply(next.n, 1, 0.50, 0.45)
        apply(next.a, 1, 0.70, 0.65)
        apply(next.n, 2, 0.90, 0.85)
        return next
      })
    }
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    update()
    return () => {
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [])

  return (
    <section className="sf-thought2" id="thought">
      <div ref={trackRef} className="sf-thought2-track">
        <div className="sf-thought2-pin">
          <div className="sf-container sf-thought2-inner">
            <div className="sf-thought2-header sf-reveal" data-sf-reveal>
              <span className="sf-eyebrow"><span className="sf-pulse" />Inside the verdict engine</span>
              <h2 className="sf-section-title">Think in patterns. <em>Decide with receipts.</em></h2>
              <p className="sf-section-lede">
                Single rules miss the picture. SecureFlow interrogates every transaction with
                parallel risk processors, rolls signals into named typologies, and lands on
                one verdict: pass, flag or block.
              </p>
            </div>

            <div className="sf-thought2-stage">
              <svg className="sf-cycle-svg" viewBox="0 0 600 440" aria-hidden="true" preserveAspectRatio="xMidYMid meet">
                <path
                  className="sf-cycle-bg"
                  d="M 60,280 A 240,240 0 0 1 540,280"
                  fill="none" stroke="rgba(15,23,42,0.08)"
                  strokeWidth="1.5" strokeDasharray="6 8"
                />
                <path
                  className={`sf-cycle-arc sf-cycle-arc-1${shown.a[0] ? ' sf-shown' : ''}`}
                  d="M 92.15,160 A 240,240 0 0 1 300,40"
                  fill="none" stroke="var(--ca-500)" strokeWidth="2" pathLength="100"
                />
                <path
                  className={`sf-cycle-arc sf-cycle-arc-2${shown.a[1] ? ' sf-shown' : ''}`}
                  d="M 300,40 A 240,240 0 0 1 507.85,160"
                  fill="none" stroke="var(--sf-500)" strokeWidth="2" pathLength="100"
                />
                <polygon
                  className={`sf-cycle-arrow sf-cycle-arrow-1${shown.a[0] ? ' sf-shown' : ''}`}
                  points="0,0 -9,-4 -9,4"
                  transform="translate(300,40)"
                  fill="var(--ca-500)"
                />
                <polygon
                  className={`sf-cycle-arrow sf-cycle-arrow-2${shown.a[1] ? ' sf-shown' : ''}`}
                  points="0,0 -9,-4 -9,4"
                  transform="translate(507.85,160) rotate(60)"
                  fill="var(--sf-500)"
                />
              </svg>

              {NODES.map((n, i) => (
                <div key={n.badge} className={`sf-cycle-node sf-n${i + 1}${shown.n[i] ? ' sf-shown' : ''}`}>
                  <span className="sf-cycle-badge">{n.badge}</span>
                  <div className="sf-cycle-text">
                    <h4>{n.title}</h4>
                    <p>{n.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .sf-thought2 { position: relative; background: var(--bg); }
        .sf-thought2-track { position: relative; height: 200vh; }
        .sf-thought2-pin {
          position: sticky; top: 0; height: 100vh;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          overflow: hidden;
          padding: max(110px,13vh) 0 max(36px,4vh);
        }
        .sf-thought2-pin::before {
          content: ''; position: absolute;
          left: 50%; top: 50%;
          width: 900px; height: 900px;
          transform: translate(-50%,-50%);
          background: radial-gradient(closest-side,rgba(34,197,94,0.05),transparent 70%);
          pointer-events: none; z-index: 0;
          animation: sfCycleAmbientBreathe 9s ease-in-out infinite;
        }
        @keyframes sfCycleAmbientBreathe {
          0%,100% { opacity: 1; transform: translate(-50%,-50%) scale(1); }
          50% { opacity: 0.7; transform: translate(-50%,-50%) scale(1.06); }
        }

        .sf-thought2-inner {
          width: 100%; position: relative; z-index: 1;
          display: grid;
          grid-template-columns: 0.85fr 1.15fr;
          gap: 60px;
          align-items: start;
          max-width: 1240px; margin: 0 auto;
          padding: 0 var(--gutter);
        }
        @media (max-width: 880px) {
          .sf-thought2-inner {
            grid-template-columns: 1fr;
            gap: 40px; max-width: 600px;
            align-items: center;
          }
        }
        .sf-thought2-header {
          text-align: left; max-width: none;
          margin: 0; padding: clamp(8px,2vh,28px) 0 0;
          flex-shrink: 0;
        }
        .sf-thought2-header :global(.sf-section-title) {
          font-size: clamp(1.7rem,3.4vw,2.7rem);
          margin-top: 14px; text-align: left;
        }
        .sf-thought2-header :global(.sf-section-lede) {
          margin: 16px 0 0;
          font-size: clamp(0.95rem,1.1vw,1.08rem);
          max-width: none; text-align: left; line-height: 1.65;
        }
        @media (max-width: 880px) {
          .sf-thought2-header { text-align: center; padding-top: 0; }
          .sf-thought2-header :global(.sf-section-title),
          .sf-thought2-header :global(.sf-section-lede) { text-align: center; }
          .sf-thought2-header :global(.sf-section-lede) {
            max-width: 60ch; margin-left: auto; margin-right: auto;
          }
        }

        .sf-thought2-stage {
          position: relative;
          width: 100%; max-width: 600px;
          aspect-ratio: 600 / 440;
          margin: 0 auto;
          flex-shrink: 0; overflow: visible;
        }
        .sf-cycle-svg {
          position: absolute; inset: 0;
          width: 100%; height: 100%; overflow: visible;
        }
        .sf-cycle-bg { animation: sfCycleBgFlow 5s linear infinite; }
        @keyframes sfCycleBgFlow { to { stroke-dashoffset: -14; } }
        .sf-cycle-arc {
          stroke-dasharray: 100; stroke-dashoffset: 100;
          transition: stroke-dashoffset 0.7s var(--ease);
        }
        .sf-cycle-arc.sf-shown { stroke-dashoffset: 0; }
        .sf-cycle-arrow {
          opacity: 0;
          transition: opacity 0.35s var(--ease) 0.55s;
        }
        .sf-cycle-arrow.sf-shown { opacity: 1; }

        .sf-cycle-node {
          position: absolute;
          width: 0; height: 0;
          opacity: 0; transform: scale(0.7);
          transform-origin: 0 0;
          transition: opacity 0.5s var(--ease),transform 0.5s var(--ease);
          z-index: 3;
        }
        .sf-n1 { top: 36.36%; left: 15.36%; }
        .sf-n2 { top: 9.09%;  left: 50%; }
        .sf-n3 { top: 36.36%; left: 84.64%; }
        .sf-cycle-node.sf-shown { opacity: 1; transform: scale(1); }

        .sf-cycle-badge {
          position: absolute; top: -26px; left: -26px;
          width: 52px; height: 52px; border-radius: 50%;
          background: #fff; border: 2px solid var(--ca-500);
          color: var(--ca-700);
          display: flex; align-items: center; justify-content: center;
          font-family: var(--mono); font-size: 14px; font-weight: 700;
          box-shadow: 0 12px 30px -8px rgba(14,165,233,0.35);
          transition: transform 0.35s var(--ease),filter 0.35s var(--ease);
        }
        .sf-n2 .sf-cycle-badge {
          border-color: var(--sf-500); color: var(--sf-700);
          box-shadow: 0 12px 30px -8px rgba(34,197,94,0.35);
        }
        .sf-n3 .sf-cycle-badge {
          border-color: var(--ca-cyan); color: var(--ca-cyan);
          box-shadow: 0 12px 30px -8px rgba(6,182,212,0.35);
        }
        .sf-cycle-node:hover .sf-cycle-badge {
          transform: scale(1.12); filter: brightness(1.08);
        }
        @keyframes sfCycleBadgeGlowBlue {
          0%,100% { box-shadow: 0 12px 30px -8px rgba(14,165,233,0.35); }
          50% { box-shadow: 0 14px 32px -6px rgba(14,165,233,0.58),0 0 0 6px rgba(14,165,233,0.08); }
        }
        @keyframes sfCycleBadgeGlowGreen {
          0%,100% { box-shadow: 0 12px 30px -8px rgba(34,197,94,0.35); }
          50% { box-shadow: 0 14px 32px -6px rgba(34,197,94,0.58),0 0 0 6px rgba(34,197,94,0.08); }
        }
        @keyframes sfCycleBadgeGlowCyan {
          0%,100% { box-shadow: 0 12px 30px -8px rgba(6,182,212,0.35); }
          50% { box-shadow: 0 14px 32px -6px rgba(6,182,212,0.58),0 0 0 6px rgba(6,182,212,0.08); }
        }
        .sf-n1.sf-shown .sf-cycle-badge { animation: sfCycleBadgeGlowBlue 4s ease-in-out infinite 0.6s; }
        .sf-n2.sf-shown .sf-cycle-badge { animation: sfCycleBadgeGlowGreen 4s ease-in-out infinite 1s; }
        .sf-n3.sf-shown .sf-cycle-badge { animation: sfCycleBadgeGlowCyan 4s ease-in-out infinite 1.4s; }

        .sf-cycle-text {
          position: absolute;
          width: 185px; min-height: 96px;
          top: 36px; left: 50%;
          transform: translateX(-50%);
          text-align: center; text-wrap: balance;
          transition: transform 0.35s var(--ease);
        }
        .sf-cycle-node:hover .sf-cycle-text {
          transform: translateX(-50%) translateY(-3px);
        }
        .sf-cycle-node h4 {
          font-size: 15px; font-weight: 600;
          color: var(--text); letter-spacing: -0.01em; line-height: 1.25;
        }
        .sf-cycle-node p {
          font-size: 12.5px; color: var(--text-3);
          line-height: 1.55; margin-top: 6px; text-wrap: pretty;
        }
        @media (max-width: 720px) {
          .sf-cycle-badge { width: 42px; height: 42px; font-size: 12px; top: -21px; left: -21px; }
          .sf-cycle-text { width: 150px; min-height: 84px; top: 30px; }
          .sf-cycle-node h4 { font-size: 13.5px; }
          .sf-cycle-node p { font-size: 11px; }
        }
      `}</style>
    </section>
  )
}
